import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from './JobDetailNew.module.css';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../../components/loader2';
import showToast from "../../components/ShowToast";
import PostcodeAutocomplete from "../../components/PostcodeAutocomplete/PostcodeAutocomplete";
import api from '../../api/api.js';

import {
  ArrowLeft,
  Calendar,
  Camera,
  DollarSign,
  Home,
  MapPin,
  Plus,
  Search,
  Upload,
  X,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  MessageSquare, // Added for quotes icon
} from "lucide-react";

const JobDetailNew = () => {
  const navigate = useNavigate();
  const { jobId } = useParams(); // For edit mode later
  const location = useLocation(); // To access route state

  const [jobTitle, setJobTitle] = useState("");
  const [postcode, setPostcode] = useState("");
  const [mystate, setMyState] = useState("");
  const [description, setDescription] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [homeUserId, setHomeUserId] = useState(null); // Added to store homeUserId
  
  const [selectedProfessions, setSelectedProfessions] = useState([]); // Will store { professionId, name }
  const [availableProfessions, setAvailableProfessions] = useState([]); // For the initial top 20 list
  const [professionSearchTerm, setProfessionSearchTerm] = useState('');
  const [professionSearchResults, setProfessionSearchResults] = useState([]);
  const [isProfessionDropdownOpen, setIsProfessionDropdownOpen] = useState(false);
  const professionDropdownRef = useRef(null);


  const [customBudget, setCustomBudget] = useState("");
  const [selectedBudgetRange, setSelectedBudgetRange] = useState("");
  const budgetRanges = [
    "$500 - $1000", "$1000 - $2500", "$2500 - $5000",
    "$5000 - $10000", "$10000 - $25000", "$25000 - $50000", "$50000+",
  ];

  const [selectedTimeline, setSelectedTimeline] = useState("");
  const timelineOptions = [
    { value: "Urgently", label: "Urgently", icon: AlertCircle, colorClass: styles.jobDetailNew_bgRed500 },
    { value: "Within 1 week", label: "Within 1 week", icon: Clock, colorClass: styles.jobDetailNew_bgBlue500 },
    { value: "Within 2 weeks", label: "Within 2 weeks", icon: Calendar, colorClass: styles.jobDetailNew_bgGreen500 },
    { value: "2 weeks or longer", label: "2 weeks or longer", icon: Calendar, colorClass: styles.jobDetailNew_bgPurple500 },
  ];

  const [siteVisit, setSiteVisit] = useState("Anytime");
  const siteVisitTimes = ["Anytime", "Weekdays only", "Weekends only", "Evenings", "By appointment"];

  const [uploadedImages, setUploadedImages] = useState([]); // Will store objects: { key, url, file, isExisting, originalServerKey }

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [currentJobData, setCurrentJobData] = useState(null); // To store fetched job data for comparison

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageInModal, setCurrentImageInModal] = useState(0);


  const handlePostcodeSelect = (location) => {
    // This function receives the object from the autocomplete component
    console.log("Selected Location:", location);
    // You can set the postcode to be the full string or just the 4 digits
    setPostcode(location.fullAddress); // e.g., "2166, Cabramatta"
    setMyState(location.state); // e.g., "NSW"

    console.log(location.state);
  };




  const openImageModal = (index) => {
    setCurrentImageInModal(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const nextImageInModal = () => {
    setCurrentImageInModal((prevIndex) => (prevIndex + 1) % uploadedImages.length);
  };

  const prevImageInModal = () => {
    setCurrentImageInModal((prevIndex) => (prevIndex - 1 + uploadedImages.length) % uploadedImages.length);
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (jobId) {
        setIsLoading(true);
        setError(null);
        try {
          const response = await axios.get(`${API_BASE_URL}/api/Jobs/getjobbyid?id=${jobId}`);
          const jobData = response.data;
          setCurrentJobData(jobData); 

          setJobTitle(jobData.name || "");
          setPostcode(jobData.postcode || "");
          setMyState(jobData.mystate || "");
          setDescription(jobData.description || "");
          setSelectedProfessions(jobData.profession ? jobData.profession.map(p => ({ professionId: p.professionId, name: p.name })) : []);
          setCustomBudget(jobData.budgetAmount ? String(jobData.budgetAmount) : "");
          setSelectedBudgetRange(jobData.budget || "");
          setSelectedTimeline(jobData.deadline || "");
          setSiteVisit(jobData.siteVisit || "Anytime");
          // Only set projectId from jobData if not already set by route state
          if (!location.state?.projectId) {
            setSelectedProjectId(jobData.projectId ? String(jobData.projectId) : "");
          }

          const initialUploadedImageObjects = [];
          for (let i = 1; i <= 4; i++) {
            const imageUrl = jobData[`image${i}`];
            if (imageUrl && imageUrl !== "https://api.buildstart.au") {
              initialUploadedImageObjects.push({
                key: `existing-${i}-${imageUrl.slice(-10)}`,
                url: imageUrl,
                file: null,
                isExisting: true,
                originalServerKey: `image${i}`
              });
            }
          }
          setUploadedImages(initialUploadedImageObjects);

          const token = localStorage.getItem('token');
          if (token) {
            const decodeToken = jwtDecode(token);
            const loggedInUserId = String(decodeToken.nameid);
            const jobOwnerId = String(jobData.userId);

            if (jobOwnerId !== loggedInUserId) {
              setIsReadOnly(true);
              showToast('info', "You are viewing this job in read-only mode as you are not the owner.");
            } else {
              setIsReadOnly(false);
            }
          } else {
            setIsReadOnly(true);
            showToast('info', "Viewing job in read-only mode. Please log in to edit.");
          }

        } catch (err) {
          console.error("Error fetching job details:", err);
          setError(err.message || "Failed to fetch job details.");
          showToast('error', `Error fetching job: ${err.message}`);
          navigate("/newhomejobs");
        } finally {
          setIsLoading(false);
        }
      } else {
        setJobTitle("");
        setPostcode("");
        setMyState("");
        setDescription("");
        setSelectedProfessions([]);
        setCustomBudget("");
        setSelectedBudgetRange("");
        setSelectedTimeline("");
        setSiteVisit("Anytime");
        setUploadedImages([]);
        setIsReadOnly(false);
        setCurrentJobData(null);
      }
    };

    fetchJobDetails();
  }, [jobId, navigate]);

  useEffect(() => {
    const fetchTopProfessions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/searchtop20professions`);
        const { tradespersons, buildingProfessionals } = response.data;
        const topTradespersons = tradespersons.slice(0, 10).map(p => ({ professionId: p.professionId, name: p.name, type: p.professionType }));
        const topBuildingProfessionals = buildingProfessionals.slice(0, 10).map(p => ({ professionId: p.professionId, name: p.name, type: p.professionType }));
        setAvailableProfessions([...topTradespersons, ...topBuildingProfessionals]);
      } catch (err) {
        console.error("Error fetching top professions:", err);
        setAvailableProfessions([
          { professionId: 158, name: "Quantity Surveyor", type: "building_professional" }, { professionId: 18, name: "Builder", type: "tradesperson" }, 
          { professionId: 70, name: "Electrician", type: "tradesperson" }, { professionId: 145, name: "Plumber", type: "tradesperson" },
          { professionId: 33, name: "Carpenter", type: "tradesperson" }, { professionId: 136, name: "Painter", type: "tradesperson" },
          { professionId: 124, name: "Landscaper", type: "tradesperson" }, { professionId: 166, name: "Roofer", type: "tradesperson" },
          { professionId: 202, name: "Tiler", type: "tradesperson" }, { professionId: 119, name: "Kitchen Designer", type: "building_professional" },
          { professionId: 6, name: "Architect", type: "building_professional" }, { professionId: 110, name: "Interior Designer", type: "building_professional" }
        ]);
      }
    };
    fetchTopProfessions();

    const fetchUserProjects = async (userId) => {
      if (!userId) return;
      try {
        const response = await api.get(`/api/Projects/getmyprojects?homeUserId=${userId}`);
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]); // Ensure projects is an empty array on error
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const currentHomeUserId = decodedToken.nameid;
      setHomeUserId(currentHomeUserId);
      fetchUserProjects(currentHomeUserId);
    }

    // Check for projectId in route state for auto-selection
    const projectFromState = location.state?.projectId;
    if (projectFromState) {
      setSelectedProjectId(String(projectFromState));
    }

  }, [jobId, location.state]); // Added location.state to dependencies

  const handleProfessionSearchChange = async (e) => {
    const searchValue = e.target.value;
    setProfessionSearchTerm(searchValue);

    if (searchValue.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/searchprofessions?search=${searchValue}`);
        setProfessionSearchResults(response.data.slice(0, 30));
        setIsProfessionDropdownOpen(true);
      } catch (error) {
        console.error('Error fetching professions:', error);
        setProfessionSearchResults([]);
      }
    } else {
      setProfessionSearchResults([]);
      setIsProfessionDropdownOpen(false);
    }
  };

  const handleSelectProfession = (profession) => {
    const profToAdd = { professionId: profession.professionId || profession.id, name: profession.name };
    if (!selectedProfessions.some(p => p.professionId === profToAdd.professionId)) {
      setSelectedProfessions([...selectedProfessions, profToAdd]);
    }
    setProfessionSearchTerm("");
    setProfessionSearchResults([]);
    setIsProfessionDropdownOpen(false);
  };

  const handleProfessionRemove = (professionIdToRemove) => {
    setSelectedProfessions(selectedProfessions.filter((p) => p.professionId !== professionIdToRemove));
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
        if (professionDropdownRef.current && !professionDropdownRef.current.contains(event.target)) {
            setIsProfessionDropdownOpen(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [professionDropdownRef]);

  const handleImageUpload = (event) => {
    const filesFromInput = Array.from(event.target.files);
    const currentTotalImages = uploadedImages.length;
    const remainingSlots = 4 - currentTotalImages;

    let actualFilesToUpload = filesFromInput;
    if (filesFromInput.length > remainingSlots) {
      showToast('error', `You can only upload ${remainingSlots} more image(s). Max 4 images allowed.`);
      actualFilesToUpload = filesFromInput.slice(0, remainingSlots);
    }

    const newImageObjects = actualFilesToUpload.map((file, index) => {
      const objectURL = URL.createObjectURL(file);
      return {
        key: `new-${Date.now()}-${index}`,
        url: objectURL,
        file: file,
        isExisting: false,
        originalServerKey: null
      };
    });
    setUploadedImages(prevUploadedImages => [...prevUploadedImages, ...newImageObjects]);
  };

  const removeImage = (indexToRemove) => {
    const imageToRemove = uploadedImages[indexToRemove];
    if (imageToRemove && !imageToRemove.isExisting && imageToRemove.url && imageToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    }
    setUploadedImages(prevUploadedImages => prevUploadedImages.filter((_, i) => i !== indexToRemove));
  };

  const handleSubmit = async (isPublish = true) => {
    if (jobTitle.trim().length < 10) { showToast('error', 'Job title must be at least 10 characters long.'); return; }
    if (description.trim().length < 10) { showToast('error', 'Job description must be at least 10 characters long.'); return; }
    if (selectedProfessions.length === 0) { showToast('error', 'Please select at least one profession.'); return; }
    if (!postcode.trim()) { showToast('error', 'Please enter a postcode.'); return; }
    if (!customBudget.trim() && !selectedBudgetRange) { showToast('error', 'Please provide a budget (custom amount or range).'); return; }
    if (!selectedTimeline) { showToast('error', 'Please select a project timeline.'); return; }
    if (!siteVisit) { showToast('error', 'Please select site visit availability.'); return; }

    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('error', 'Please Login to create or update a job.');
      setIsLoading(false);
      return;
    }

    try {
      const decodeToken = jwtDecode(token);
          const homeUserIdFromToken = parseInt(decodeToken.nameid, 10);
          setHomeUserId(homeUserIdFromToken); // Ensure homeUserId is set

      if (jobId) { // Edit operation
        const jobDataToUpdate = {
          job_id: parseInt(jobId, 10),
          projectId: selectedProjectId ? parseInt(selectedProjectId, 10) : null,
          userId: homeUserIdFromToken,
          name: jobTitle,
          profession: selectedProfessions.map(p => ({ ProfessionId: parseInt(p.professionId, 10), name: p.name })),
          postcode: postcode,
          mystate: currentJobData?.mystate || "", // Keep existing state if not updated
          description: description,
          budgetAmount: customBudget ? parseFloat(customBudget) : null,
          budget: selectedBudgetRange,
          deadlineDate: currentJobData?.deadlineDate || null,
          deadline: selectedTimeline,
          siteVisit: siteVisit,
          jobStatus: currentJobData?.jobStatus || "Open", // Keep existing status if not explicitly changed
          created_at: currentJobData?.created_at,
          updated_at: new Date().toISOString(),
          isPublish: isPublish,
          isNew: false,
          image1: null, image2: null, image3: null, image4: null,
        };

        for (let i = 0; i < 4; i++) {
          const imgObj = uploadedImages[i];
          if (imgObj) {
            if (imgObj.isExisting) {
              jobDataToUpdate[`image${i + 1}`] = imgObj.url.replace(/^https?:\/\/[^/]+\//, "/");
            } else {
              jobDataToUpdate[`image${i + 1}`] = ""; 
            }
          } else {
            jobDataToUpdate[`image${i + 1}`] = null;
          }
        }
        
        console.log("Submitting job update with payload:", jobDataToUpdate);
        await axios.post(`${API_BASE_URL}/api/Jobs/updatejob?id=${jobId}`, jobDataToUpdate);

        const newFilesFormData = new FormData();
        uploadedImages.forEach(imgObj => {
          if (imgObj.file && !imgObj.isExisting) {
            newFilesFormData.append("files", imgObj.file);
          }
        });

        if (newFilesFormData.has("files")) {
          console.log("Uploading new images for job:", jobId);
          await axios.post(`${API_BASE_URL}/api/Jobs/upload/${jobId}`, newFilesFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        
        showToast('success', 'Job updated successfully!');
        if (jobDataToUpdate.projectId) {
          console.log(`Navigating to project ${jobDataToUpdate.projectId} with job ${jobDataToUpdate.job_id}`);
          navigate(`/project/${jobDataToUpdate.projectId}`, { state: { selectedJobId: jobDataToUpdate.job_id } });
        } else {
          navigate("/newhomejobs");
        }

      } else { // Create new job operation
        const createJobPayload = {
            projectId: selectedProjectId ? parseInt(selectedProjectId, 10) : null,
            userId: homeUserIdFromToken,
            name: jobTitle,
            profession: selectedProfessions.map(p => ({ ProfessionId: parseInt(p.professionId, 10), name: p.name })),
            postcode: postcode,
            mystate: mystate,
            description: description,
            budgetAmount: customBudget ? parseFloat(customBudget) : null,
            budget: selectedBudgetRange,
            deadlineDate: null, 
            deadline: selectedTimeline,
            siteVisit: siteVisit,
            jobStatus: "Open", 
            created_at: new Date().toISOString(), 
            updated_at: new Date().toISOString(),
            isPublish: isPublish,
            isNew: true 
        };
        console.log("Creating new job with payload:", createJobPayload);
        const response = await api.post(`/api/Jobs/createjob`, createJobPayload);

        console.log(response);

        const newJobId = response.data.job_id;

        if (newJobId && uploadedImages.length > 0) {
          const formData = new FormData();
          uploadedImages.forEach(imgObj => {
            if (imgObj.file && !imgObj.isExisting) {
              formData.append("files", imgObj.file);
            }
          });
          
          if (formData.has("files")) {
              console.log("Uploading images for new job:", newJobId);
              await axios.post(`${API_BASE_URL}/api/Jobs/upload/${newJobId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
          }
        }
        
        showToast('success', 'Job created successfully!');
        if (createJobPayload.projectId) {
          const newJobId = response.data.job_id;
          console.log(`Navigating to project ${createJobPayload.projectId} with new job ${newJobId}`);
          navigate(`/project/${createJobPayload.projectId}`, { state: { selectedJobId: newJobId } });
        } else {
          navigate("/newhomejobs");
        }
      }

    } catch (err) {
      console.error(`Error ${jobId ? 'updating' : 'creating'} job:`, err.response || err);
      const action = jobId ? 'update' : 'create';
      setError(err.response?.data?.message || err.message || `Failed to ${action} job.`);
      showToast('error', `Error: ${err.response?.data?.message || err.message || `An unexpected error occurred while trying to ${action} the job.`}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>
      <header className={styles.jobDetailNew_header}>
        <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
          <div className={styles.jobDetailNew_headerLeft}>
            <button onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{color: 'white'}}>
              <ArrowLeft className={styles.jobDetailNew_iconMd} />
            </button>
            <div className={styles.jobDetailNew_headerLogo}>
              <Home className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber400}`} />
              <span className={styles.jobDetailNew_headerTitle}>Build Start</span>
            </div>
            <div className={styles.jobDetailNew_headerSeparator}></div>
            <div>
              <h1 className={styles.jobDetailNew_headerTitle}>{jobId ? "Edit Job Details" : "Create New Job"}</h1>
              <p className={styles.jobDetailNew_textBlue200}>{jobId ? "Update your project requirements" : "Enter your project details"}</p>
            </div>
          </div>
        </div>
      </header>

      <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
        {isLoading && <Loader2 />}
        {error && <p style={{color: 'red'}}>{error}</p>}
        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_lg_gridCols3}`}>
          <div className={`${styles.jobDetailNew_lg_colSpan2} ${styles.jobDetailNew_spaceY6}`}>
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Search className={styles.jobDetailNew_iconMd} />
                  <span>Who are you looking for?</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <div className={styles.jobDetailNew_inputWithIconContainer} ref={professionDropdownRef}>
                  <Search className={styles.jobDetailNew_inputIcon} />
                  <input 
                    type="text" 
                    placeholder="Search for professionals (e.g., Electrician)" 
                    className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                    value={professionSearchTerm}
                    onChange={handleProfessionSearchChange}
                    onFocus={() => !isReadOnly && professionSearchResults.length > 0 && setIsProfessionDropdownOpen(true)}
                    disabled={isReadOnly}
                  />
                  {isProfessionDropdownOpen && professionSearchResults.length > 0 && !isReadOnly && (
                    <ul className={styles.jobDetailNew_dropdownResults} ref={professionDropdownRef}>
                      {professionSearchResults.map((item) => (
                        <li
                          key={item.professionId || item.id}
                          className={styles.jobDetailNew_resultItem}
                          onClick={() => handleSelectProfession(item)}
                        >
                          {item.name} ({item.professionType || item.type || 'N/A'})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <label className={styles.jobDetailNew_label}>Selected Professions:</label>
                  <div className={styles.jobDetailNew_flexWrap}>
                    {selectedProfessions.map((profession) => (
                      <span key={profession.professionId} className={styles.jobDetailNew_badge}>
                        {profession.name}
                        {!isReadOnly && (
                          <button
                            type="button"
                            className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}
                            onClick={() => handleProfessionRemove(profession.professionId)}
                          >
                            <X className={styles.jobDetailNew_buttonIcon} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={styles.jobDetailNew_label}>Suggested Professions:</label>
                  <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols3}`}>
                    {availableProfessions
                      .filter((prof) => !selectedProfessions.some(sp => sp.professionId === prof.professionId))
                      .map((profession) => (
                        <button
                          type="button"
                          key={profession.professionId}
                          className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline} ${styles.jobDetailNew_buttonOutlineSm} ${styles.jobDetailNew_buttonJustifyStart}`}
                          onClick={() => !isReadOnly && handleSelectProfession(profession)}
                          disabled={isReadOnly}
                        >
                          <Plus className={`${styles.jobDetailNew_buttonIcon} ${styles.jobDetailNew_buttonIconMr2}`} />
                          {profession.name}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <FileText className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />
                  <span>Job Information</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="jobTitle" className={styles.jobDetailNew_label}>Job Title</label>
                    <input id="jobTitle" type="text" placeholder="e.g., Kitchen Renovation" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="project" className={styles.jobDetailNew_label}>Assign to Project (Optional)</label>
                    <select id="project" value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className={styles.jobDetailNew_selectTrigger} disabled={isReadOnly}>
                      <option value="">Select a project</option>
                      {projects.map((project) => (
                        <option key={project.projectId} value={project.projectId}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="postcode" className={styles.jobDetailNew_label}>Postcode</label>


                  <PostcodeAutocomplete
                    value={postcode}
                    onSuggestionSelect={handlePostcodeSelect} // Pass the new handler
                    disabled={isReadOnly}
                  />


                  </div>
                </div>
                <div>
                  <label htmlFor="description" className={styles.jobDetailNew_label}>Job Description</label>
                  <textarea id="description" placeholder="Describe your project in detail..." value={description} onChange={(e) => setDescription(e.target.value)} className={styles.jobDetailNew_textarea} rows={4} disabled={isReadOnly}></textarea>
                </div>
              </div>
            </div>

            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <DollarSign className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textGreen500}`} />
                  <span>Budget Information</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="customBudget" className={styles.jobDetailNew_label}>Custom Budget Amount (Optional)</label>
                    <div className={styles.jobDetailNew_inputWithIconContainer}>
                      <DollarSign className={styles.jobDetailNew_inputIcon} />
                      <input id="customBudget" type="number" placeholder="Enter amount" value={customBudget} onChange={(e) => setCustomBudget(e.target.value)} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} disabled={isReadOnly} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="budgetRange" className={styles.jobDetailNew_label}>Budget Range</label>
                    <select id="budgetRange" value={selectedBudgetRange} onChange={(e) => setSelectedBudgetRange(e.target.value)} className={styles.jobDetailNew_selectTrigger} disabled={isReadOnly}>
                      <option value="">Select a range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Clock className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />
                  <span>Project Timeline</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <label className={styles.jobDetailNew_label}>When do you need this job done by?</label>
                <div className={styles.jobDetailNew_timelineButtonGrid}>
                  {timelineOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <button
                        type="button"
                        key={option.value}
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${selectedTimeline === option.value ? option.colorClass : styles.jobDetailNew_buttonOutline}`}
                        onClick={() => !isReadOnly && setSelectedTimeline(option.value)}
                        disabled={isReadOnly}
                      >
                        <IconComponent className={styles.jobDetailNew_timelineButtonIcon} />
                        <span className={styles.jobDetailNew_timelineButtonText}>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Calendar className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textPurple500}`} />
                  <span>Site Visit Availability</span>
                </h3>
              </div>
              <div className={styles.jobDetailNew_cardContent}>
                <div>
                  <label htmlFor="siteVisit" className={styles.jobDetailNew_label}>When are you available for site visits?</label>
                  <select id="siteVisit" value={siteVisit} onChange={(e) => setSiteVisit(e.target.value)} className={styles.jobDetailNew_selectTrigger} disabled={isReadOnly}>
                    {siteVisitTimes.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                  <span>Project Images</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>Upload images to help businesses provide accurate quotes (Max 4 images)</p>
                <div className={styles.jobDetailNew_uploadArea}>
                  <Upload className={styles.jobDetailNew_uploadIcon} />
                  <p className={`${styles.jobDetailNew_textGray600} ${styles.jobDetailNew_mb2}`}>Drag and drop files here</p>
                  <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray500} ${styles.jobDetailNew_mb4}`}>or</p>
                  <label htmlFor="file-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{cursor: isReadOnly ? 'default' : 'pointer'}}>
                    Browse Files
                    <input id="file-upload" type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} disabled={isReadOnly} />
                  </label>
                </div>
                {uploadedImages.length > 0 && (
                  <div>
                    <label className={styles.jobDetailNew_label}>Uploaded Images ({uploadedImages.length})</label>
                    <div className={styles.jobDetailNew_uploadedImagesGrid}>
                      {uploadedImages.map((imageObject, index) => (
                        <div key={imageObject.key} className={styles.jobDetailNew_uploadedImageItem} onClick={() => openImageModal(index)} style={{cursor: 'pointer'}}>
                          <img src={imageObject.url} alt={`Upload ${index + 1}`} className={styles.jobDetailNew_uploadedImage} />
                          {!isReadOnly && (
                            <button 
                              type="button" 
                              className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_imageRemoveBtn}`} 
                              onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                            >
                              <X className={styles.jobDetailNew_buttonIcon} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.jobDetailNew_spaceY6}>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_sidebarCard}`}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_sidebarHeader}`}>
                <h3 className={styles.jobDetailNew_sidebarTitle}>Job Summary</h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_sidebarContentSpaceY4}`}>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>TITLE</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{jobTitle || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>LOCATION</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{postcode || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>BUDGET</label>
                    <p className={`${styles.jobDetailNew_sidebarValue} ${styles.jobDetailNew_sidebarBudgetValue}`}>{customBudget ? `$${customBudget}` : (selectedBudgetRange || "Not specified")}</p>
                    {customBudget && selectedBudgetRange && <p className={styles.jobDetailNew_sidebarBudgetRange}>Range: {selectedBudgetRange}</p>}
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>TIMELINE</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedTimeline || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>PROFESSIONALS</label>
                    <div className={styles.jobDetailNew_flexWrap} style={{marginTop: '0.25rem'}}>
                      {selectedProfessions.length > 0 ? selectedProfessions.map((prof) => (
                        <span key={prof.professionId} className={`${styles.jobDetailNew_badge} ${styles.jobDetailNew_sidebarProfessionBadge}`}>
                          {prof.name}
                        </span>
                      )) : <p className={styles.jobDetailNew_sidebarValue}>Not specified</p>}
                    </div>
                  </div>
                  {currentJobData?.assignedBusinessId && currentJobData?.businessName && (
                    <div>
                      <label className={styles.jobDetailNew_sidebarLabel}>ASSIGNED TO</label>
                      <div className={styles.jobDetailNew_assignedBusinessInfo}>
                        {/* You might want to add an avatar or logo if available in currentJobData.businessProfilePic */}
                        <p className={styles.jobDetailNew_sidebarValue}>{currentJobData.businessName}</p>
                        <span className={`${styles.jobDetailNew_badge} ${styles.jobDetailNew_assignedBadge}`}>Assigned</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.jobDetailNew_separator}></div>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  {!isReadOnly && jobId && currentJobData && currentJobData.quoteCount > 0 && (
                    <button 
                      type="button" 
                      onClick={() => navigate(`/job/${jobId}/quotes`)} 
                      className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonSecondary}`} // Using a secondary style, adjust if needed
                    >
                      <MessageSquare className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      View Quotes ({currentJobData.quoteCount})
                    </button>
                  )}
                  {!isReadOnly && (
                    <button type="button" onClick={() => handleSubmit(true)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonPrimary}`}>
                      <CheckCircle className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      {jobId ? "Save Changes" : "Post Job"}
                    </button>
                  )}
                  <button type="button" onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonOutline}`}>
                    <ArrowLeft className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                    Go Back
                  </button>
                </div>
              </div>
            </div>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_tipsCard}`}>
              <div className={styles.jobDetailNew_tipsContent}>
                <div className={styles.jobDetailNew_tipsEmoji}>💡</div>
                <h3 className={styles.jobDetailNew_tipsTitle}>Pro Tips</h3>
                <ul className={styles.jobDetailNew_tipsList}>
                  <li>• Add detailed descriptions for better quotes</li>
                  <li>• Upload clear photos of the work area</li>
                  <li>• Set realistic budgets and timelines</li>
                  <li>• Be specific about your requirements</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isImageModalOpen && uploadedImages.length > 0 && uploadedImages[currentImageInModal] && (
        <div className={styles.jobDetailNew_imageModalBackdrop} onClick={closeImageModal}>
          <div className={styles.jobDetailNew_imageModalContent} onClick={(e) => e.stopPropagation()}>
            <button onClick={closeImageModal} className={styles.jobDetailNew_imageModalCloseBtn}>
              <X size={24} />
            </button>
            <button onClick={prevImageInModal} className={`${styles.jobDetailNew_imageModalNavBtn} ${styles.jobDetailNew_imageModalPrevBtn}`}>
              {'<'}
            </button>
            <img src={uploadedImages[currentImageInModal].url} alt={`View ${currentImageInModal + 1}`} className={styles.jobDetailNew_imageModalImage} />
            <button onClick={nextImageInModal} className={`${styles.jobDetailNew_imageModalNavBtn} ${styles.jobDetailNew_imageModalNextBtn}`}>
              {'>'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailNew;
